import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { CSSProperties } from 'styled-components';
import styled from 'styled-components';

import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Progress } from '@sorare/core/src/atoms/loader/Progress';
import { ThresholdLabel } from '@sorare/core/src/atoms/ui/SplitProgressBar/ThresholdLabel';
import { Score } from '@sorare/core/src/components/collections/Score';
import { getCollectionsTeamShield } from '@sorare/core/src/lib/collections';

import type { ProgressBar_cardCollection } from './__generated__/index.graphql.ts';

const Root = styled(Horizontal)`
  align-items: flex-start;
`;
const BarWrapper = styled(Vertical).attrs({ center: true })`
  padding-top: var(--half-unit);
  flex: 1;
`;
const Separator = styled.div`
  background: var(--c-nd-150);
  width: 1px;
  height: var(--intermediate-unit);
`;

const insertIconScoreIntoThresholds = (
  thresholds: ProgressBar_cardCollection['bonusThresholds'],
  teamShieldScore: number
) => {
  let index = 0;
  for (let i = 1; i < thresholds.length; i += 1) {
    if (teamShieldScore > thresholds[i].threshold) {
      index = i + 1;
    }
  }

  return [
    ...thresholds.slice(0, index),
    { threshold: teamShieldScore, bonus: null },
    ...thresholds.slice(index),
  ];
};

type Props = {
  cardCollection: Nullable<ProgressBar_cardCollection>;
  score: number;
};
export const ProgressBar = ({ cardCollection, score }: Props) => {
  const { bonusThresholds } = cardCollection || {};
  const {
    pictureUrl,
    score: teamShieldScore,
    name,
  } = getCollectionsTeamShield(cardCollection);

  if (!bonusThresholds?.length) {
    return null;
  }

  const thresholdsWithIcon =
    pictureUrl && teamShieldScore
      ? insertIconScoreIntoThresholds(bonusThresholds, teamShieldScore)
      : bonusThresholds;

  const nextBonusThresholdIndex = thresholdsWithIcon.findIndex(
    bt => bt.threshold > score
  );

  const nextBonusThreshold =
    nextBonusThresholdIndex !== -1
      ? thresholdsWithIcon[nextBonusThresholdIndex]
      : thresholdsWithIcon[thresholdsWithIcon.length - 1];
  const previousBonusThreshold =
    nextBonusThresholdIndex !== -1
      ? thresholdsWithIcon[nextBonusThresholdIndex - 1] || {
          threshold: 0,
          bonus: 0,
        }
      : thresholdsWithIcon[thresholdsWithIcon.length - 2] || {
          threshold: 0,
          bonus: 0,
        };

  const barLength = nextBonusThreshold.threshold;
  const progress = score;
  const previousThresholdCrossed = score >= previousBonusThreshold.threshold;
  const nextThresholdCrossed = score >= nextBonusThreshold.threshold;

  return (
    <Root>
      <ThresholdLabel
        shiny={previousThresholdCrossed}
        faded={nextThresholdCrossed}
        bonus={previousBonusThreshold.bonus}
        badge={
          typeof previousBonusThreshold.bonus === 'number'
            ? undefined
            : { pictureUrl, name }
        }
      />
      <BarWrapper>
        <Progress
          style={
            {
              '--progress-color':
                'linear-gradient(to right, #FFB800 0%, #F0CE1D 37.72%, #FCFF63 100%)',
            } as CSSProperties
          }
          value={progress - previousBonusThreshold.threshold}
          max={barLength - previousBonusThreshold.threshold}
        />
        <Horizontal>
          <Score score={score} rarity={cardCollection?.rarity} />
          {!nextThresholdCrossed && (
            <>
              <Separator />
              <Score
                score={nextBonusThreshold.threshold}
                color="var(--c-nd-600)"
              />
            </>
          )}
        </Horizontal>
      </BarWrapper>
      <ThresholdLabel
        shiny={nextThresholdCrossed}
        bonus={nextBonusThreshold.bonus}
        badge={nextBonusThreshold.bonus ? undefined : { pictureUrl, name }}
      />
    </Root>
  );
};

ProgressBar.fragments = {
  cardCollection: gql`
    fragment ProgressBar_cardCollection on CardCollection {
      slug
      rarity
      bonusThresholds {
        threshold
        bonus
      }
      ...getCollectionsShield_cardCollection
    }
    ${getCollectionsTeamShield.fragments.cardCollection}
  ` as TypedDocumentNode<ProgressBar_cardCollection>,
};
